import type React from 'react';
import { createSlot } from '@ayocore/platform/client/extensions/createSlot';
import type { DealExtended } from './DealViewController';

/** Payload, передаваемый во все слоты DealView */
export interface DealViewPayload {
  deal: DealExtended;
  reload: () => void;
}

/** Описание дополнительной вкладки */
export interface DealTabExtension {
  /** Уникальный ключ вкладки */
  value: string;
  /** Отображаемый заголовок вкладки */
  label: string;
  /** Содержимое панели вкладки */
  content: React.ReactNode;
  /**
   * Если указан — заменяет встроенную вкладку с этим ключом вместо добавления новой.
   * Допустимые ключи встроенных вкладок: 'rows', 'documents', 'expenses'.
   * (Вкладка «Закупки» — не встроенная: её добавляет модуль procurement.)
   *
   * @example
   * // Заменить вкладку "Позиции" своей реализацией:
   * { overrides: 'rows', value: 'elita-rows', label: 'Позиции', content: <MyPanel /> }
   */
  overrides?: string;
  /**
   * Если true — встроенная вкладка скрывается (не рендерится).
   * Используется вместе с overrides для условного скрытия вкладок по типу сделки.
   *
   * @example
   * // Скрыть вкладку "Счета" для определённого типа сделок:
   * { overrides: 'invoices', value: 'hide-invoices', label: '', hidden: true, content: null }
   */
  hidden?: boolean;
}

/**
 * Точки расширения DealView.
 *
 * Позволяют клиентским модулям добавлять контент в DealView
 * без изменения базового кода CRM модуля.
 *
 * Регистрация происходит в bootstrap.ts клиентского модуля.
 *
 * @example
 * // src/modules/grasias/client/bootstrap.ts
 * import { DealViewExtensions } from '@ayocore/erp/crm/client/components/Deals/views/DealViewExtensions';
 *
 * // Добавить новую вкладку после встроенных:
 * DealViewExtensions.tabs.register(({ deal, reload }) => ({
 *   value: 'grasias-accounting',
 *   label: 'Учёт',
 *   content: <GrasiasAccountingTab deal={deal} onSuccess={reload} />,
 * }));
 *
 * // Заменить встроенную вкладку "Позиции" своей реализацией
 * // (только для нужного deal type, иначе вернуть null):
 * DealViewExtensions.tabs.register(({ deal, reload }) => {
 *   if (deal.deal_type?.slug !== 'MY_TYPE') return null;
 *   return {
 *     value: 'my-rows',
 *     label: 'Позиции',
 *     overrides: 'rows',
 *     content: <MyPositionsPanel deal={deal} onReload={reload} />,
 *   };
 * });
 *
 * // Кнопка в заголовке
 * DealViewExtensions.headerActions.register(({ deal, reload }) => (
 *   <GrasiasDealButton deal={deal} onSuccess={reload} />
 * ));
 *
 * // Блок в основной вкладке
 * DealViewExtensions.mainPanelBlocks.register(({ deal }) => (
 *   <GrasiasDealInfo deal={deal} />
 * ));
 */
export const DealViewExtensions = {
  /** Дополнительные кнопки в заголовке (рядом с меню действий) */
  headerActions: createSlot<DealViewPayload>(),

  /** Дополнительные вкладки (добавляются после базовых) или замена встроенных (если указан overrides) */
  tabs: createSlot<DealViewPayload, DealTabExtension | null>(),

  /** Дополнительные блоки в конце основной вкладки */
  mainPanelBlocks: createSlot<DealViewPayload>(),

  /**
   * Скрытие панели событий (event list) для определённых типов сделок.
   * Если хотя бы один обработчик возвращает true — event list не отображается.
   */
  hideEventList: createSlot<DealViewPayload, boolean>(),
};
